import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Animated,
  AccessibilityInfo,
  Dimensions,
  Platform,
} from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { Ionicons } from '@expo/vector-icons';
import AnimatedWave from './AnimatedWave';

interface HeaderGlassProps {
  title: string;
  subtitle?: string;
  userName?: string;
  isLive: boolean;
  lastUpdate?: Date;
  isRTL: boolean;
  isArabic: boolean;
  systemStatus?: 'operational' | 'attention' | 'critical';
  criticalCount?: number;
  offlineCount?: number;
}

const { width: SCREEN_WIDTH } = Dimensions.get('window');

export default function HeaderGlass({
  title,
  subtitle,
  userName,
  isLive,
  lastUpdate,
  isRTL,
  isArabic,
  systemStatus = 'operational',
  criticalCount = 0,
  offlineCount = 0,
}: HeaderGlassProps) {
  // Animation values
  const contentOpacity = useRef(new Animated.Value(0)).current;
  const twinkleAnim = useRef(new Animated.Value(0)).current;

  // Reduce motion (accessibility)
  const [reduceMotion, setReduceMotion] = useState(false);

  // Galaxy Stats
  const [stars] = useState(() =>
    Array.from({ length: 40 }).map((_, i) => ({
      id: i,
      top: Math.random() * 100,
      left: Math.random() * 100,
      size: Math.random() * 2 + 1,
      opacity: Math.random() * 0.5 + 0.3,
    })),
  );

  // Check accessibility setting once
  useEffect(() => {
    let mounted = true;

    AccessibilityInfo.isReduceMotionEnabled()
      .then((enabled) => {
        if (mounted) setReduceMotion(!!enabled);
      })
      .catch(() => {
        // if failed, keep animations enabled by default
      });

    // Fade in content on mount
    Animated.timing(contentOpacity, {
      toValue: 1,
      duration: 400,
      useNativeDriver: true,
    }).start();

    // Twinkle Effect
    Animated.loop(
      Animated.sequence([
        Animated.timing(twinkleAnim, {
          toValue: 1,
          duration: 2000,
          useNativeDriver: true,
        }),
        Animated.timing(twinkleAnim, {
          toValue: 0,
          duration: 2000,
          useNativeDriver: true,
        }),
      ]),
    ).start();

    return () => {
      mounted = false;
    };
  }, [contentOpacity]);

  const getStatusColor = () => {
    if (systemStatus === 'critical' || criticalCount > 0) return '#ef4444'; // Red
    if (systemStatus === 'attention' || offlineCount > 0) return '#f59e0b'; // Amber
    return '#10b981'; // Emerald
  };

  const statusColor = getStatusColor();

  return (
    <View style={styles.container}>
      {/* Galaxy Background */}
      <LinearGradient
        colors={['#050A30', '#0b1654', '#1e1b4b']}
        start={{ x: 0, y: 0 }}
        end={{ x: 0, y: 1 }}
        style={StyleSheet.absoluteFill}
      />

      {/* Stars Layer */}
      {stars.map((s) => (
        <Animated.View
          key={s.id}
          style={[
            styles.star,
            {
              top: `${s.top}%`,
              left: `${s.left}%`,
              width: s.size,
              height: s.size,
              borderRadius: s.size / 2,
              opacity: twinkleAnim.interpolate({
                inputRange: [0, 0.5, 1],
                outputRange: [s.opacity, s.opacity * 0.4, s.opacity],
              }),
            },
          ]}
        />
      ))}

      {/* Nebula/Wave Effect at Bottom */}
      <View style={styles.waveContainer}>
        <AnimatedWave height={60} amplitude={15} speed={0.3} color="#4f46e5" opacity={0.15} />
        <View style={{ marginTop: -60 }}>
          <AnimatedWave height={50} amplitude={10} speed={0.5} color="#a5f3fc" opacity={0.1} flip />
        </View>
      </View>

      {/* Glass Content Layer */}
      <Animated.View style={[styles.content, { opacity: contentOpacity }]}>
        <View style={[styles.headerRow, isRTL ? styles.rowRTL : styles.rowLTR]}>
          {/* Title Section */}
          <View style={styles.titleContainer}>
            {userName && (
              <Text style={[styles.greeting, isRTL && styles.textRTL]}>
                {isArabic ? 'مرحباً' : 'Hello'}, {userName}
              </Text>
            )}
            <Text style={[styles.title, isRTL && styles.textRTL]}>{title}</Text>
            {subtitle && <Text style={[styles.subtitle, isRTL && styles.textRTL]}>{subtitle}</Text>}

            {/* Live Indicator */}
            <View style={[styles.liveBadge, isRTL ? styles.rowRTL : styles.rowLTR]}>
              <View style={[styles.statusDot, { backgroundColor: statusColor }]} />
              <Text style={styles.liveText}>
                {isLive ? (isArabic ? 'متصل' : 'ONLINE') : isArabic ? 'غير متصل' : 'OFFLINE'}
              </Text>
              {lastUpdate && (
                <Text style={styles.lastUpdate}>
                  •{' '}
                  {lastUpdate.toLocaleTimeString(isArabic ? 'ar-SA' : 'en-US', {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </Text>
              )}
            </View>
          </View>
        </View>
      </Animated.View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    height: 180, // Slightly taller for better nebula effect
    position: 'relative',
    overflow: 'hidden',
    borderBottomLeftRadius: 30,
    borderBottomRightRadius: 30,
    shadowColor: '#4f46e5',
    shadowOffset: { width: 0, height: 10 },
    shadowOpacity: 0.3,
    shadowRadius: 20,
    elevation: 10,
    zIndex: 100,
  },
  waveContainer: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: 80,
    justifyContent: 'flex-end',
    opacity: 0.8,
  },
  star: {
    position: 'absolute',
    backgroundColor: '#fff',
    shadowColor: '#fff',
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.8,
    shadowRadius: 2,
    elevation: 2,
  },
  content: {
    flex: 1,
    paddingTop: Platform.OS === 'ios' ? 50 : 40, // Status bar padding
    paddingHorizontal: 20,
  },
  headerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  rowRTL: {
    flexDirection: 'row',
  },
  rowLTR: {
    flexDirection: 'row',
  },
  titleContainer: {
    flex: 1,
    justifyContent: 'center',
  },
  greeting: {
    fontSize: 14,
    color: '#a5f3fc',
    opacity: 0.9,
    marginBottom: 2,
    fontWeight: '500',
  },
  title: {
    fontSize: 28,
    fontWeight: '800',
    color: '#ffffff',
    textShadowColor: 'rgba(79, 70, 229, 0.6)',
    textShadowOffset: { width: 0, height: 0 },
    textShadowRadius: 10,
    marginBottom: 4,
  },
  subtitle: {
    fontSize: 14,
    color: '#a5f3fc', // Cyan-200
    opacity: 0.8,
    marginBottom: 8,
    fontWeight: '500',
  },
  liveBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 12,
    alignSelf: 'flex-start',
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.15)',
  },
  statusDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    marginHorizontal: 6,
    shadowColor: '#fff',
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.5,
    shadowRadius: 4,
  },
  liveText: {
    fontSize: 10,
    fontWeight: '700',
    color: '#ffffff',
    letterSpacing: 0.5,
  },
  lastUpdate: {
    fontSize: 10,
    color: 'rgba(255, 255, 255, 0.6)',
    marginLeft: 4,
  },
  textRTL: {
    textAlign: 'right',
  },
});
